// @ts-nocheck

// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Ticket } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SpectatorTicket {
  id: string;
  name: string;
  price: number;
  thumbnail_url?: string;
}

interface SpectatorTicketsBoxProps {
  eventId: string;
  eventSlug?: string;
  ticketDisplayText?: string;
}

const SpectatorTicketsBox: React.FC<SpectatorTicketsBoxProps> = ({ eventId, eventSlug, ticketDisplayText }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SpectatorTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState<string>('');

  useEffect(() => {
    const fetchSpectatorTickets = async () => {
      try {
        console.log('🎟️ Fetching spectator tickets for event:', eventId);
        
        // Fetch event pricing and event name from new modular system
        const [pricingResult, eventResult] = await Promise.all([
          supabase
            .from('event_pricing')
            .select('pricing_structure')
            .eq('event_id', eventId)
            .single(),
          supabase
            .from('events')
            .select('name')
            .eq('id', eventId)
            .single()
        ]);

        if (pricingResult.error) {
          console.error('❌ Error fetching event pricing:', pricingResult.error);
          return;
        }

        if (eventResult.error) {
          console.error('❌ Error fetching event name:', eventResult.error);
        } else {
          setEventName(eventResult.data.name || '');
        }

        // Extract spectator tickets from pricing_structure JSONB
        const spectatorTickets = pricingResult.data?.pricing_structure?.spectator_tickets || [];
        console.log('🎟️ Spectator tickets from pricing_structure:', spectatorTickets);
        
        // Filter to only active tickets with price > 0, and format data
        const activeTickets = spectatorTickets
          .filter((ticket: any) => ticket.is_active && ticket.price > 0)
          .map((ticket: any) => ({
            id: ticket.id || `ticket-${ticket.name}`, // Use ticket.id if available, fallback to name-based ID
            name: ticket.name,
            price: ticket.price / 100, // Convert cents to dollars
            thumbnail_url: ticket.thumbnail_url
          }));
        
        console.log('🎟️ Active spectator tickets found:', activeTickets.length);
        console.log('🎟️ Formatted tickets:', activeTickets);
        
        setTickets(activeTickets);
        
        if (activeTickets.length > 0) {
          console.log('✅ Active spectator tickets available for purchase');
        } else {
          console.log('ℹ️ No active spectator tickets found for this event');
        }
      } catch (error) {
        console.error('❌ Exception fetching spectator tickets:', error);
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchSpectatorTickets();
    } else {
      console.log('⚠️ No eventId provided to SpectatorTicketsBox');
      setLoading(false);
    }
  }, [eventId]);

  // Show loading state
  if (loading) {
    console.log('🔄 SpectatorTicketsBox loading...');
    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow flex flex-col">
        <CardHeader className="flex-1">
          <Ticket className="w-8 h-8 mx-auto mb-2 text-primary" />
          <h3 className="font-medium text-sm">Loading tickets...</h3>
        </CardHeader>
      </Card>
    );
  }

  // Check if we have active spectator tickets
  const hasActiveTickets = tickets.length > 0;

  if (hasActiveTickets) {
    console.log('✅ Rendering SpectatorTicketsBox with active tickets:', tickets.length);
    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow flex flex-col">
        <CardHeader className="flex-1">
          <Ticket className="w-8 h-8 mx-auto mb-2 text-primary" />
          <h3 className="font-medium text-sm">🎟️ Tickets</h3>
          <p className="text-xs text-gray-600 mt-1">
            Purchase spectator tickets for admission to this event
          </p>
          {ticketDisplayText && (
            <p className="text-xs text-gray-600 mt-2 border-t pt-2">
              {ticketDisplayText}
            </p>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <Button 
            variant="default" 
            onClick={() => navigate(`/events/${eventId}/spectator-tickets`)}
            className="w-full"
          >
            Buy Now
          </Button>
        </CardContent>
      </Card>
    );
  } else {
    console.log('📝 Rendering fallback generic tickets box');
    // Fallback: Generic tickets placeholder
    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow flex flex-col">
        <CardHeader className="flex-1">
          <Ticket className="w-8 h-8 mx-auto mb-2 text-primary" />
          <h3 className="font-medium text-sm">Tickets</h3>
          {ticketDisplayText && (
            <p className="text-xs text-gray-600 mt-2">
              {ticketDisplayText}
            </p>
          )}
        </CardHeader>
      </Card>
    );
  }
};

export default SpectatorTicketsBox;